<template>
  <admin-modal size="lg" :title="`Approve ${doctor.doctorname}`"
               subtitle="Assign the doctor to a clinic. Approving emails their login details."
               :busy="saving" @close="$emit('close')">
    <div v-if="error" class="adm-alert adm-alert--error"><i class="fas fa-exclamation-circle"></i><div>{{ error }}</div></div>

    <div class="adm-approve-doctor">
      <span class="adm-avatar adm-avatar--sm">{{ initials(doctor.doctorname) }}</span>
      <div>
        <span class="adm-person__name">{{ doctor.doctorname }}</span>
        <span class="adm-person__meta">
          {{ doctor.specialization || "Specialization not set" }} · {{ doctor.email || "No email" }}
        </span>
      </div>
    </div>

    <div v-if="registered.name" class="adm-approve-registered">
      <i class="fas fa-clinic-medical" aria-hidden="true"></i>
      <div>
        <span class="adm-approve-registered__label">Clinic entered at registration</span>
        <strong>{{ registered.name }}</strong>
        <span v-if="registered.address" class="adm-muted"> · {{ registered.address }}</span>
        <span v-if="!loading" class="adm-approve-registered__hint">
          {{ matchIds.size ? "A clinic with a similar name is listed first below." : "No existing clinic matches this name." }}
        </span>
      </div>
      <button v-if="!loading && !matchIds.size && mode !== 'new'" type="button" class="adm-btn adm-btn--sm" @click="mode = 'new'">
        <i class="fas fa-plus"></i> Create this clinic
      </button>
    </div>
    <div v-else class="adm-approve-registered adm-approve-registered--none">
      <i class="fas fa-clinic-medical" aria-hidden="true"></i>
      <div>No clinic name was entered at registration.</div>
    </div>

    <div v-if="linked.length" class="adm-alert adm-alert--info">
      <i class="fas fa-info-circle"></i>
      <div>
        Already assigned to
        <strong v-for="(clinic, i) in linked" :key="clinic.clinicid">{{ clinic.clinicname }}{{ i < linked.length - 1 ? ", " : "" }}</strong>.
        You can approve now or add another clinic.
      </div>
    </div>

    <div class="adm-tabs adm-approve-tabs" role="tablist">
      <button type="button" class="adm-tab" :class="{ 'is-active': mode === 'existing' }" role="tab"
              :aria-selected="mode === 'existing'" @click="mode = 'existing'">
        <i class="fas fa-hospital"></i> Assign existing clinic
      </button>
      <button type="button" class="adm-tab" :class="{ 'is-active': mode === 'new' }" role="tab"
              :aria-selected="mode === 'new'" @click="mode = 'new'">
        <i class="fas fa-plus"></i> Create new clinic
      </button>
    </div>

    <div v-if="mode === 'existing'">
      <div class="adm-search adm-approve-search">
        <i class="fas fa-search"></i>
        <input v-model.trim="search" class="adm-input" placeholder="Search clinics by name, ID or city" />
      </div>
      <div v-if="loading" class="adm-options">
        <div v-for="n in 3" :key="n" class="adm-option"><div class="adm-skeleton" style="width: 60%"></div></div>
      </div>
      <div v-else-if="!clinics.length" class="adm-empty">
        <i class="fas fa-hospital"></i>
        <strong>No clinics yet</strong>
        Create the doctor's clinic in the “Create new clinic” tab.
      </div>
      <div v-else-if="!filteredClinics.length" class="adm-empty">No clinic matches “{{ search }}”.</div>
      <div v-else class="adm-options">
        <label v-for="clinic in filteredClinics" :key="clinic.clinicid" class="adm-option"
               :class="{ 'is-checked': isChecked(clinic), 'is-disabled': isLinked(clinic) }">
          <input type="checkbox" :checked="isChecked(clinic)" :disabled="isLinked(clinic)"
                 @change="toggle(clinic.clinicid)" />
          <span class="adm-option__text">
            <span class="adm-option__title">{{ clinic.clinicname }}</span>
            <span class="adm-option__meta">
              <span class="adm-mono">{{ clinic.clinicid }}</span> · {{ clinic.locationname || "No city" }}
              · {{ clinic.doctor_count }} doctor{{ clinic.doctor_count === 1 ? "" : "s" }}
            </span>
          </span>
          <span v-if="isLinked(clinic)" class="adm-badge adm-badge--info adm-badge--plain">Assigned</span>
          <span v-else-if="matchIds.has(clinic.clinicid)" class="adm-badge adm-badge--active adm-badge--plain">Matches registration</span>
        </label>
      </div>
    </div>

    <div v-else>
      <p v-if="registered.name" class="adm-muted adm-approve-prefill">
        <i class="fas fa-magic"></i> Pre-filled from the doctor's registration. Check the details before approving.
      </p>
      <clinic-form-fields v-model="newClinic" :cities="cities" :errors="clinicErrors" />
    </div>

    <template #footer>
      <span class="adm-muted adm-approve-summary">{{ summary }}</span>
      <button type="button" class="adm-btn" :disabled="saving" @click="$emit('close')">Cancel</button>
      <button type="button" class="adm-btn adm-btn--success" :disabled="saving || loading || !canApprove" @click="approve">
        <span v-if="saving" class="adm-spinner"></span>
        <i v-else class="fas fa-check"></i>
        Approve &amp; send login
      </button>
    </template>
  </admin-modal>
</template>

<script>
import axios from "axios";
import AdminModal from "./AdminModal.vue";
import ClinicFormFields, { clinicPayload, emptyClinic, suggestClinicId, validateClinic } from "./ClinicFormFields.vue";
import { errorMessage, initials } from "../admin-utils";

// Words that say nothing about which clinic it is, ignored when matching names.
const GENERIC = new Set(["clinic", "clinics", "hospital", "hospitals", "hosp", "the", "and", "centre", "center"]);

/** "Mayura Clinic, " -> "mayura" (lowercase, letters/digits only, generic words dropped). */
function nameCore(name) {
  return (name || "").toLowerCase().split(/[^a-z0-9]+/).filter(w => w && !GENERIC.has(w)).join(" ");
}

/** Doctor rows store "Not provided" for an empty registration field. */
function provided(value) {
  const v = (value || "").replace(/\s+/g, " ").trim();
  return v && v.toLowerCase() !== "not provided" ? v : "";
}

export default {
  name: "ApproveDoctorModal",
  components: { AdminModal, ClinicFormFields },
  props: {
    doctor: { type: Object, required: true },
    cities: { type: Array, default: () => [] },
  },
  emits: ["close", "approved"],
  data() {
    return {
      mode: "existing",
      loading: true,
      saving: false,
      error: "",
      clinics: [],
      linked: [],
      selected: [],
      search: "",
      newClinic: this.registrationClinic(),
      clinicErrors: {},
    };
  },
  computed: {
    linkedIds() {
      return new Set(this.linked.map(c => c.clinicid));
    },
    registered() {
      return { name: provided(this.doctor.clincorhospname), address: provided(this.doctor.address) };
    },
    // Existing clinics whose name looks like the one the doctor registered with.
    matchIds() {
      const core = nameCore(this.registered.name);
      if (!core) return new Set();
      return new Set(this.clinics.filter(c => {
        const other = nameCore(c.clinicname);
        if (!other) return false;
        if (other === core) return true;
        const [short, long] = other.length < core.length ? [other, core] : [core, other];
        return short.length >= 4 && long.includes(short);
      }).map(c => c.clinicid));
    },
    filteredClinics() {
      const term = this.search.toLowerCase();
      const list = !term ? this.clinics : this.clinics.filter(c =>
        [c.clinicname, c.clinicid, c.locationname].some(v => (v || "").toLowerCase().includes(term)));
      // Matches for the registration clinic first, otherwise keep the server order.
      return [...list].sort((a, b) => this.matchIds.has(b.clinicid) - this.matchIds.has(a.clinicid));
    },
    canApprove() {
      if (this.mode === "new") return true; // validated on submit
      return this.selected.length > 0 || this.linked.length > 0;
    },
    summary() {
      if (this.mode === "new") return this.newClinic.clinicname ? `Creates “${this.newClinic.clinicname}”` : "";
      const count = this.selected.length;
      return count ? `${count} clinic${count === 1 ? "" : "s"} selected` : "";
    },
  },
  watch: {
    // Cities may arrive after the modal opens; fill the doctor's city in then.
    cities() {
      if (!this.newClinic.city_id) this.newClinic = this.withCity(this.newClinic);
    },
  },
  methods: {
    initials,
    // The new-clinic form, pre-filled with what the doctor gave at registration.
    registrationClinic() {
      const d = this.doctor;
      const form = {
        ...emptyClinic(),
        clinicname: provided(d.clincorhospname).slice(0, 100),
        clinicaddress: provided(d.address).slice(0, 500),
        contactnumbers: provided(d.contactnumbers).slice(0, 50),
        email: provided(d.email).slice(0, 100),
      };
      return this.withCity(form);
    },
    withCity(form) {
      const city = this.cities.find(c => String(c.city_id) === String(this.doctor.city_id));
      const next = city ? { ...form, city_id: city.city_id, locationname: city.name.slice(0, 25) } : { ...form };
      if (next.clinicname) next.clinicid = suggestClinicId(next, this.cities);
      return next;
    },
    isLinked(clinic) {
      return this.linkedIds.has(clinic.clinicid);
    },
    toggle(clinicid) {
      const i = this.selected.indexOf(clinicid);
      if (i === -1) this.selected.push(clinicid);
      else this.selected.splice(i, 1);
    },
    isChecked(clinic) {
      return this.isLinked(clinic) || this.selected.includes(clinic.clinicid);
    },
    async load() {
      this.loading = true;
      try {
        const [clinics, linked] = await Promise.all([
          axios.get("/admin/clinics"),
          axios.get(`/admin/doctors/${this.doctor.doctorid}/clinics`),
        ]);
        this.clinics = clinics.data || [];
        this.linked = linked.data || [];
        if (!this.clinics.length) this.mode = "new";
      } catch (error) {
        this.error = errorMessage(error, "Could not load clinics.");
      } finally {
        this.loading = false;
      }
    },
    async approve() {
      this.error = "";
      const body = {};
      if (this.mode === "new") {
        this.clinicErrors = validateClinic(this.newClinic);
        if (Object.keys(this.clinicErrors).length) return;
        body.new_clinic = clinicPayload(this.newClinic);
      } else {
        body.clinicids = this.selected;
      }
      this.saving = true;
      try {
        const { data } = await axios.post(`/admin/doctors/${this.doctor.doctorid}/approve`, body);
        this.$emit("approved", data);
      } catch (error) {
        this.error = errorMessage(error, "Could not approve the doctor.");
      } finally {
        this.saving = false;
      }
    },
  },
  mounted() {
    this.load();
  },
};
</script>

<style scoped>
.adm-approve-doctor {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 14px;
  border: 1px solid var(--adm-line);
  border-radius: var(--adm-radius-sm);
  background: #f9fafb;
  margin-bottom: 16px;
}

.adm-approve-registered {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 14px;
  border: 1px solid #b2ddff;
  background: #eff8ff;
  border-radius: var(--adm-radius-sm);
  margin-bottom: 16px;
  font-size: 14px;
}

.adm-approve-registered > i {
  color: var(--adm-primary);
  font-size: 18px;
}

.adm-approve-registered > div {
  flex: 1;
  min-width: 0;
}

.adm-approve-registered--none {
  border-color: var(--adm-line);
  background: #f9fafb;
  color: var(--adm-muted);
}

.adm-approve-registered--none > i {
  color: var(--adm-muted);
}

.adm-approve-registered__label {
  display: block;
  font-size: 12px;
  color: var(--adm-muted);
}

.adm-approve-registered__hint {
  display: block;
  font-size: 12.5px;
  color: var(--adm-ink-2);
}

.adm-approve-prefill {
  font-size: 13px;
  margin: 0 0 10px;
}

.adm-approve-tabs {
  margin-bottom: 16px;
}

.adm-approve-search {
  max-width: none;
  margin-bottom: 12px;
}

.adm-approve-summary {
  margin-right: auto;
  align-self: center;
  font-size: 13px;
}
</style>
